import debug from "debug";
import ContextMenuUI from "./ContextMenuUI";
import ContextMenuController from "./ContextMenuController";

const log = debug("app:fn");

/**
 * Class representing a context menu for a 3D experience.
 * @category Components
 * @subcategory ContextMenu
 */
class ContextMenu {
  /**
   * Creates a new ContextMenu instance.
   * @param {Object} experience - The experience object containing event hub, selection manager, and other utilities.
   */
  constructor(experience) {
    /**
     * The experience object.
     * @type {Object}
     */
    this.experience = experience;

    /**
     * The event hub for communication across components.
     * @type {Object}
     */
    this.eventHub = experience.eventHub;

    /**
     * The selection manager for handling selected objects.
     * @type {Object}
     */
    this.selectionManager = experience.selectionManager;

    /**
     * The currently selected object.
     * @type {?Object}
     */
    this.selectedObject = null;

    /**
     * The UI controller for the context menu.
     * @type {ContextMenuUI}
     */
    this.contextMenuUI = new ContextMenuUI(experience);

    /**
     * The logic controller for the context menu.
     * @type {ContextMenuController}
     */
    this.contextMenuController = new ContextMenuController(experience);

    this.init();
  }

  /**
   * Initializes event listeners for the context menu.
   */
  init() {
    this.eventHub.on("rightClickContextMenu", (data) => {
      const userData = this.getPartNumberUserData();
      log("userdata", userData);
      const mergedData = { ...data, ...userData };
      this.showContextMenu(mergedData);
      log("data", mergedData);
    });

    this.eventHub.on("globalClick", () => {
      this.hideContextMenu();
    });

    this.eventHub.on("globalPan", () => {
      this.hideContextMenu();
    });

    this.eventHub.on("copyCM", () => {
      log("copy event triggered");
      this.eventHub.emit("contextMenu:paste");
    });
  }


  cleanup() {
    console.log("ContextMenu cleanup started...");
  
    // 1. Remove event listeners from eventHub
    if (this.handleRightClick) {
      this.eventHub.off("rightClickContextMenu", this.handleRightClick);
    }
    if (this.handleGlobalClick) {
      this.eventHub.off("globalClick", this.handleGlobalClick);
    }
    if (this.handleGlobalPan) {
      this.eventHub.off("globalPan", this.handleGlobalPan);
    }
    if (this.handleCopyCM) {
      this.eventHub.off("copyCM", this.handleCopyCM);
    }
  
    // 2. Dispose UI and Controller
    if (this.contextMenuUI && typeof this.contextMenuUI.dispose === "function") {
      this.contextMenuUI.dispose();
    }
    if (this.contextMenuController && typeof this.contextMenuController.dispose === "function") {
      this.contextMenuController.dispose();
    }
  
    // 3. Clear object references
    this.contextMenuUI = null;
    this.contextMenuController = null;
    this.selectedObject = null;
    this.selectionManager = null;
    this.experience = null;
  
    console.log("ContextMenu cleanup completed.");
  }

  /**
   * Displays the context menu with the provided data.
   * @param {Object} data - The data to display in the context menu.
   */
  showContextMenu(data) {
    this.eventHub.emit("contextMenu:show", data);
  }

  /**
   * Hides the context menu.
   */
  hideContextMenu() {
    this.eventHub.emit("contextMenu:hide");
  }

  /**
   * Retrieves user data for the currently selected part number.
   * @returns {?Object} The user data for the selected part number, or null if not available.
   */
  getPartNumberUserData() {
    log(
      "selectionManager.selectedObjects",
      this.selectionManager.selectedObjects
    );

    if (
      !this.selectionManager.selectedObjects ||
      this.selectionManager.selectedObjects.size === 0
    ) {
      log("No selected objects");
      return null;
    }

    const selectedObject = [...this.selectionManager.selectedObjects][0];

    if (!selectedObject || !selectedObject.userData) {
      log("Invalid selected object or missing userData");
      return null;
    }

    const partNumber = selectedObject.userData.partNumber;

    if (!partNumber) {
      log("Missing partNumber in userData");
      return null;
    }

    const productData = this.experience.dataVault?.parts?.get(partNumber);

    if (!productData) {
      log(`No product data found for partNumber: ${partNumber}`);
      return null;
    }

    log("Product Data:", productData);
    return productData;
  }
}

export default ContextMenu;
